import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSalon } from '../../contexts/SalonContext';
import { apiService } from '../../services/api';
import './UserProfile.css';

const ROLE_LABEL = { owner: 'Owner', manager: 'Manager', staff: 'Staff', receptionist: 'Receptionist' };
const GENDER_LABEL = { male: 'Male', female: 'Female', other: 'Other' };

const UserProfile = () => {
    const { user: authUser, logout } = useAuth();
    const { t, language } = useLanguage();
    const { businesses: contextBusinesses, switchBusiness, currentBusiness } = useSalon();
    const [profile, setProfile] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const fileInputRef = useRef(null);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);

    const handleLogout = () => {
        logout();
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== 'DELETE') return;
        try {
            setDeleting(true);
            await apiService.deleteAccount();
            localStorage.removeItem('authToken');
            localStorage.removeItem('currentBusinessId');
            window.location.href = '/login';
        } catch (err) {
            alert('\u274c ' + (err.message || 'Failed to delete account'));
            setDeleting(false);
        }
    };

    // Create Business state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createName, setCreateName] = useState('');
    const [createDesc, setCreateDesc] = useState('');
    const [createPhone, setCreatePhone] = useState('');
    const [createAddress, setCreateAddress] = useState('');
    const [creating, setCreating] = useState(false);
    const [createMsg, setCreateMsg] = useState({ text: '', type: '' });

    const handleCreateBusiness = async () => {
        if (!createName.trim()) {
            setCreateMsg({ text: '❌ Please enter a business name.', type: 'error' });
            return;
        }
        try {
            setCreating(true);
            setCreateMsg({ text: '', type: '' });
            const result = await apiService.createBusiness({
                name: createName.trim(),
                description: createDesc.trim(),
                phone: createPhone.trim(),
                address: createAddress.trim(),
                active: true,
            });
            setCreateMsg({ text: '✅ Business created! Refreshing...', type: 'success' });
            setTimeout(() => { window.location.reload(); }, 1500);
        } catch (err) {
            setCreateMsg({ text: '❌ ' + (err.message || 'Failed to create business'), type: 'error' });
        } finally {
            setCreating(false);
        }
    };

    // Join business state
    const [showJoinModal, setShowJoinModal] = useState(false);
    const [joinId, setJoinId] = useState('');
    const [joining, setJoining] = useState(false);
    const [joinMsg, setJoinMsg] = useState({ text: '', type: '' });

    // Leave business state
    const [leavingBizId, setLeavingBizId] = useState(null);
    const [leaving, setLeaving] = useState(false);

    // Delete business state
    const [deletingBizId, setDeletingBizId] = useState(null);
    const [deletingBiz, setDeletingBiz] = useState(false);

    // Fetch profile from API on mount
    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const data = await apiService.getProfile();
            setProfile(data);
            setFormData(data);
        } catch (err) {
            console.error('Failed to load profile:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleJoinBusiness = async () => {
        if (!joinId.trim()) {
            setJoinMsg({ text: '❌ Please enter a Business ID.', type: 'error' });
            return;
        }
        try {
            setJoining(true);
            setJoinMsg({ text: '', type: '' });
            const result = await apiService.joinBusiness(joinId.trim());
            setJoinMsg({ text: '✅ ' + result.message, type: 'success' });
            setJoinId('');
            // Refresh profile to show new business
            setTimeout(() => {
                fetchProfile();
                setShowJoinModal(false);
                setJoinMsg({ text: '', type: '' });
            }, 2000);
        } catch (err) {
            setJoinMsg({ text: '❌ ' + (err.message || 'Failed to join business'), type: 'error' });
        } finally {
            setJoining(false);
        }
    };

    const handleLeaveBusiness = async (bizId) => {
        try {
            setLeaving(true);
            const result = await apiService.leaveBusiness(bizId);
            setSaveMsg(`✅ ${result.message}`);
            setTimeout(() => setSaveMsg(''), 3000);
            setLeavingBizId(null);
            
            // If we left the current business, clear it from localStorage
            const curr = localStorage.getItem('currentBusinessId');
            if (curr === bizId) {
                localStorage.removeItem('currentBusinessId');
            }
            
            // Reload to update context correctly
            setTimeout(() => { window.location.reload(); }, 1500);
        } catch (err) {
            alert('❌ ' + (err.message || 'Failed to leave business'));
            setLeavingBizId(null);
        } finally {
            setLeaving(false);
        }
    };

    const handleDeleteBiz = async (bizId) => {
        try {
            setDeletingBiz(true);
            const result = await apiService.deleteBusiness(bizId);
            setSaveMsg(`✅ ${result.message || 'Business deleted successfully'}`);
            setTimeout(() => setSaveMsg(''), 3000);
            setDeletingBizId(null);
            
            // Clear current business from localStorage if deleted
            const curr = localStorage.getItem('currentBusinessId');
            if (curr === bizId) {
                localStorage.removeItem('currentBusinessId');
            }
            
            // Reload so App.tsx redirects to onboarding if they have 0 businesses left
            setTimeout(() => { window.location.href = '/'; }, 1500);
        } catch (err) {
            alert('❌ ' + (err.message || 'Failed to delete business'));
            setDeletingBizId(null);
        } finally {
            setDeletingBiz(false);
        }
    };

    const handleEditToggle = () => {
        if (isEditing) {
            // Cancel — revert
            setFormData(profile);
        }
        setIsEditing(!isEditing);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setSaveMsg('❌ Image too large. Max 5MB.');
            setTimeout(() => setSaveMsg(''), 3000);
            return;
        }

        const localUrl = URL.createObjectURL(file);
        setAvatarPreview(localUrl);

        try {
            setUploadingAvatar(true);
            setSaveMsg('⏳ Uploading photo...');

            // Upload avatar without business context requirement
            const token = localStorage.getItem('authToken');
            const formData = new FormData();
            formData.append('image', file);
            const uploadRes = await fetch(`${apiService.getBaseUrl()}/upload/avatar`, {
                method: 'POST',
                headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                body: formData,
            });
            if (!uploadRes.ok) {
                const err = await uploadRes.json().catch(() => ({}));
                throw new Error(err.message || 'Upload failed');
            }
            const { url } = await uploadRes.json();

            const result = await apiService.updateProfile({ avatar: url });
            setProfile(result.user);
            setFormData(result.user);
            setAvatarPreview(null);
            setSaveMsg('✅ Profile photo updated!');
            setTimeout(() => setSaveMsg(''), 3000);
        } catch (err) {
            setAvatarPreview(null);
            setSaveMsg('❌ ' + (err.message || 'Upload failed'));
            setTimeout(() => setSaveMsg(''), 4000);
        } finally {
            setUploadingAvatar(false);
            e.target.value = '';
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const { name, username, phone, dob, gender, address, avatar } = formData;
            const result = await apiService.updateProfile({ name, username, phone, dob, gender, address, avatar });
            setProfile(result.user);
            setFormData(result.user);
            setIsEditing(false);
            setSaveMsg('✅ Profile updated successfully!');
            setTimeout(() => setSaveMsg(''), 3000);
        } catch (err) {
            console.error('Save error:', err);
            setSaveMsg('❌ ' + (err.message || 'Failed to save'));
            setTimeout(() => setSaveMsg(''), 4000);
        } finally {
            setSaving(false);
        }
    };

    const handleCopyId = () => {
        const uid = profile?.id || profile?._id || '';
        navigator.clipboard.writeText(uid).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const getAvatar = () => {
        if (avatarPreview) return avatarPreview;
        const src = formData?.avatar || profile?.avatar;
        if (src) return src;
        return profile?.gender === 'female' ? '/assets/avatars/female.png' : '/assets/avatars/male.png';
    };

    const formatId = (rawId) => {
        if (!rawId) return '—';
        const id = rawId.toString();
        // Show first 8 chars uppercase for display, like a game tag
        return '#' + id.slice(-8).toUpperCase();
    };

    const formatJoinDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const getPrimaryRole = () => {
        if (!profile?.businesses?.length) return null;
        return ROLE_LABEL[profile.businesses[0]?.role] || profile.businesses[0]?.role;
    };

    if (loading) return (
        <div className="gp-loading">
            <div className="gp-spinner" />
            <span>Loading account...</span>
        </div>
    );

    if (!profile) return (
        <div className="gp-loading"><span>Unable to load profile.</span></div>
    );

    const roleLabel = getPrimaryRole();

    return (
        <div className="gp-container">
            {/* ── ACCOUNT CARD ── */}
            <div className="gp-card">

                {/* Cover banner */}
                <div className="gp-banner">
                    <div className="gp-banner-pattern" />
                </div>

                {/* Header row: avatar + name + id */}
                <div className="gp-header-row">
                    {/* Avatar — always clickable to change */}
                    <div
                        className="gp-avatar-wrap editable"
                        onClick={() => !uploadingAvatar && fileInputRef.current.click()}
                        title={uploadingAvatar ? 'Uploading...' : 'Click to change photo'}
                        style={{ cursor: uploadingAvatar ? 'wait' : 'pointer' }}
                    >
                        <img
                            src={getAvatar()}
                            alt="avatar"
                            className="gp-avatar-img"
                            style={{ opacity: uploadingAvatar ? 0.5 : 1, transition: 'opacity 0.2s' }}
                            onError={e => { e.target.src = '/assets/avatars/male.png'; }}
                        />
                        {uploadingAvatar ? (
                            <div className="gp-avatar-overlay" style={{ background: 'rgba(0,0,0,0.5)' }}>
                                <div className="gp-spinner" style={{ width: 24, height: 24, borderTopColor: '#fff' }} />
                            </div>
                        ) : (
                            <div className="gp-avatar-overlay">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                    <circle cx="12" cy="13" r="4" />
                                </svg>
                            </div>
                        )}
                        <input ref={fileInputRef} type="file" accept="image/*" className="gp-hidden-input" onChange={handleFileChange} />
                    </div>

                    <div className="gp-header-info">
                        <div className="gp-name-row">
                            <h2 className="gp-display-name">{profile.name || 'Unnamed User'}</h2>
                            {roleLabel && <span className="gp-role-badge">{roleLabel}</span>}
                        </div>
                        {profile.username && (
                            <p className="gp-username">@{profile.username}</p>
                        )}
                        <p className="gp-email">{profile.email}</p>

                        {/* Unique Account ID — like a game tag */}
                        <div className="gp-uid-row">
                            <span className="gp-uid-label">Account ID</span>
                            <code className="gp-uid-code">{formatId(profile.id || profile._id)}</code>
                            <button className="gp-copy-btn" onClick={handleCopyId} title="Copy full ID">
                                {copied ? (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                                ) : (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                                )}
                            </button>
                            <span className="gp-joined">Member since {formatJoinDate(profile.createdAt)}</span>
                        </div>
                    </div>

                    {/* Edit / Save / Logout buttons */}
                    <div className="gp-header-actions">
                        {isEditing ? (
                            <>
                                <button className="gp-btn-ghost" onClick={handleEditToggle} disabled={saving}>Cancel</button>
                                <button className="gp-btn-primary" onClick={handleSave} disabled={saving}>
                                    {saving ? 'Saving…' : 'Save Changes'}
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="gp-btn-primary" onClick={handleEditToggle}>
                                    ✏️ Edit Profile
                                </button>
                                {!showLogoutConfirm ? (
                                    <button className="gp-btn-logout" onClick={() => setShowLogoutConfirm(true)} title={language === 'en' ? 'Logout' : 'Đăng xuất'}>
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                            <polyline points="16 17 21 12 16 7" />
                                            <line x1="21" y1="12" x2="9" y2="12" />
                                        </svg>
                                        {language === 'en' ? 'Logout' : 'Đăng xuất'}
                                    </button>
                                ) : (
                                    <div className="gp-logout-confirm">
                                        <span>{language === 'en' ? 'Are you sure?' : 'Bạn chắc chưa?'}</span>
                                        <button className="gp-btn-logout-yes" onClick={handleLogout}>{language === 'en' ? 'Yes' : 'Có'}</button>
                                        <button className="gp-btn-ghost-sm" onClick={() => setShowLogoutConfirm(false)}>{language === 'en' ? 'No' : 'Không'}</button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Save message */}
                {saveMsg && <div className="gp-save-msg">{saveMsg}</div>}

                {/* ── INFO PANELS ── */}
                <div className="gp-panels">

                    {/* Personal Info */}
                    <div className="gp-panel">
                        <div className="gp-panel-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                            Personal Info
                        </div>
                        <div className="gp-fields">
                            <Field label="Full Name" name="name" value={formData.name} isEditing={isEditing} onChange={handleChange} />
                            <Field label="Username" name="username" value={formData.username} isEditing={isEditing} onChange={handleChange} placeholder="e.g. johndoe" />
                            <Field label="Email" value={profile.email} isEditing={false} readOnly />
                            <Field label="Phone" name="phone" value={formData.phone} isEditing={isEditing} onChange={handleChange} type="tel" />
                            <Field label="Date of Birth" name="dob" value={formData.dob} isEditing={isEditing} onChange={handleChange} type="date" />
                            <div className="gp-field">
                                <span className="gp-field-label">Gender</span>
                                {isEditing ? (
                                    <select name="gender" className="gp-field-input" value={formData.gender || 'male'} onChange={handleChange}>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                    </select>
                                ) : (
                                    <span className="gp-field-value">{GENDER_LABEL[profile.gender] || '—'}</span>
                                )}
                            </div>
                            <Field label="Address" name="address" value={formData.address} isEditing={isEditing} onChange={handleChange} fullWidth />
                        </div>
                    </div>

                    {/* Account Info */}
                    <div className="gp-panel">
                        <div className="gp-panel-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                            Account Details
                        </div>
                        <div className="gp-account-details">
                            <div className="gp-detail-row">
                                <span className="gp-detail-label">Full Account ID</span>
                                <code className="gp-detail-mono">{profile.id || profile._id}</code>
                            </div>
                            <div className="gp-detail-row">
                                <span className="gp-detail-label">Account Status</span>
                                <span className="gp-badge-active">● Active</span>
                            </div>
                            <div className="gp-detail-row">
                                <span className="gp-detail-label">Service Plan</span>
                                <span className="gp-detail-value" style={{textTransform: 'capitalize', fontWeight: 600, color: profile.plan === 'pro' ? '#7c3aed' : '#475569'}}>
                                    {profile.plan === 'pro' ? '👑 Pro' : '🌱 Basic'}
                                </span>
                            </div>
                            <div className="gp-detail-row">
                                <span className="gp-detail-label">Role</span>
                                <span className="gp-detail-value">{roleLabel || 'User'}</span>
                            </div>
                            <div className="gp-detail-row">
                                <span className="gp-detail-label">Businesses</span>
                                <span className="gp-detail-value">{profile.businesses?.length || 0} linked</span>
                            </div>
                            <div className="gp-detail-row">
                                <span className="gp-detail-label">Member Since</span>
                                <span className="gp-detail-value">{formatJoinDate(profile.createdAt)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── BUSINESSES ── */}
                <div className="gp-biz-section">
                    <div className="gp-biz-header">
                        <div className="gp-panel-title" style={{ margin: 0 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                            My Businesses
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button className="gp-btn-create" onClick={() => { setShowCreateModal(true); setCreateMsg({ text: '', type: '' }); setCreateName(''); setCreateDesc(''); setCreatePhone(''); setCreateAddress(''); }}>
                                🏢 Get Your Own Business
                            </button>
                            <button className="gp-btn-join" onClick={() => { setShowJoinModal(true); setJoinMsg({ text: '', type: '' }); setJoinId(''); }}>
                                🔗 Join a Business
                            </button>
                        </div>
                    </div>

                    {profile.businesses?.length > 0 ? (
                        <div className="gp-biz-grid">
                            {profile.businesses.map((biz, i) => {
                                // Find business name from SalonContext businesses list
                                const bizId = biz.businessId?.toString();
                                const fullBiz = contextBusinesses.find(b => b.id === bizId || b._id === bizId);
                                const bizName = fullBiz?.name || biz.name || null;

                                return (
                                    <div
                                        key={i}
                                        className={`gp-biz-card gp-biz-card--clickable${currentBusiness?.id === bizId ? ' gp-biz-card--active' : ''}`}
                                        onClick={() => bizId && switchBusiness(bizId)}
                                        title={`Switch to ${bizName || bizId}`}
                                    >
                                        <div className="gp-biz-icon">🏢</div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                {bizName && (
                                                    <div className="gp-biz-name">{bizName}</div>
                                                )}
                                                {currentBusiness?.id === bizId && (
                                                    <span style={{
                                                        fontSize: '0.65rem',
                                                        fontWeight: 700,
                                                        background: 'linear-gradient(135deg,#667eea,#764ba2)',
                                                        color: '#fff',
                                                        padding: '2px 8px',
                                                        borderRadius: '20px',
                                                        letterSpacing: '0.03em',
                                                        whiteSpace: 'nowrap'
                                                    }}>📍 {language === 'vi' ? 'Đang làm việc' : 'You are here'}</span>
                                                )}
                                            </div>
                                            <div className="gp-biz-id">ID: <code>{bizId?.slice(-8).toUpperCase()}</code></div>
                                            <div className="gp-biz-role">
                                                <span className={`gp-biz-role-badge ${biz.role}`}>{ROLE_LABEL[biz.role] || biz.role}</span>
                                            </div>
                                            <div className="gp-biz-joined">Joined {formatJoinDate(biz.joinedAt)}</div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                            {currentBusiness?.id !== bizId && (
                                                <div className="gp-biz-switch-hint">Switch →</div>
                                            )}
                                            {biz.role === 'owner' ? (
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); setDeletingBizId({ id: bizId, name: bizName }); }}
                                                    style={{
                                                        background: 'none', border: '1px solid #fee2e2', color: '#dc2626',
                                                        padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem',
                                                        cursor: 'pointer', marginTop: 'auto', fontWeight: 600
                                                    }}
                                                >
                                                    {language === 'vi' ? 'Xóa Business' : 'Delete'}
                                                </button>
                                            ) : (
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); setLeavingBizId({ id: bizId, name: bizName }); }}
                                                    style={{
                                                        background: 'none', border: '1px solid #f3f4f6', color: '#6b7280',
                                                        padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem',
                                                        cursor: 'pointer', marginTop: 'auto'
                                                    }}
                                                >
                                                    {language === 'vi' ? 'Rời đi' : 'Leave'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="gp-biz-empty">
                            <span>🏢</span>
                            <p>You haven't joined any business yet.</p>
                            <p className="gp-biz-empty-hint">Create a new business or join an existing one using its Business ID.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* ── DANGER ZONE ── */}
            <div style={{
                marginTop: '24px',
                border: '1px solid #fee2e2',
                borderRadius: '12px',
                padding: '20px 24px',
                background: '#fff5f5'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '18px' }}>⚠️</span>
                    <h3 style={{ margin: 0, color: '#b91c1c', fontSize: '1rem' }}>
                        {language === 'vi' ? 'Vùng nguy hiểm' : 'Danger Zone'}
                    </h3>
                </div>
                <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '0 0 16px' }}>
                    {language === 'vi'
                        ? 'Xoá tài khoản sẽ xóa vĩnh viễn toàn bộ dữ liệu của bạn. Hành động này không thể hoàn tác.'
                        : 'Deleting your account will permanently erase all your data. This action cannot be undone.'}
                </p>
                {!showDeleteConfirm ? (
                    <button
                        onClick={() => setShowDeleteConfirm(true)}
                        style={{
                            background: 'none', border: '1.5px solid #ef4444',
                            color: '#ef4444', padding: '8px 18px', borderRadius: '8px',
                            cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
                            transition: 'all 0.2s'
                        }}
                    >
                        🗑️ {language === 'vi' ? 'Xóa tài khoản' : 'Delete My Account'}
                    </button>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '360px' }}>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#7f1d1d', fontWeight: 600 }}>
                            {language === 'vi'
                                ? 'Gõ DELETE để xác nhận:'
                                : 'Type DELETE to confirm:'}
                        </p>
                        <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={e => setDeleteConfirmText(e.target.value)}
                            placeholder="DELETE"
                            style={{
                                padding: '8px 12px', border: '1.5px solid #ef4444',
                                borderRadius: '8px', fontSize: '0.9rem',
                                outline: 'none', width: '100%'
                            }}
                        />
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={handleDeleteAccount}
                                disabled={deleteConfirmText !== 'DELETE' || deleting}
                                style={{
                                    background: deleteConfirmText === 'DELETE' ? '#ef4444' : '#fca5a5',
                                    color: '#fff', border: 'none',
                                    padding: '8px 18px', borderRadius: '8px',
                                    cursor: deleteConfirmText === 'DELETE' ? 'pointer' : 'not-allowed',
                                    fontWeight: 700, fontSize: '0.875rem'
                                }}
                            >
                                {deleting ? (language === 'vi' ? 'Đang xóa...' : 'Deleting...') : (language === 'vi' ? 'Xác nhận xóa' : 'Confirm Delete')}
                            </button>
                            <button
                                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                                style={{
                                    background: 'none', border: '1.5px solid #94a3b8',
                                    color: '#475569', padding: '8px 18px', borderRadius: '8px',
                                    cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem'
                                }}
                            >
                                {language === 'vi' ? 'Hủy' : 'Cancel'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── CREATE BUSINESS MODAL ── */}
            {showCreateModal && (
                <div className="gp-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}>
                    <div className="gp-modal" style={{ maxWidth: '480px' }}>
                        <div className="gp-modal-header">
                            <h3>🏢 Get Your Own Business</h3>
                            <button className="gp-modal-close" onClick={() => setShowCreateModal(false)}>✕</button>
                        </div>
                        <div className="gp-modal-body">
                            <div className="gp-join-info">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                <div>Create a new business that you own. You can own multiple businesses — each gets its own ID, employees, and booking link.</div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                                <div>
                                    <label className="gp-join-label">Business Name <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input type="text" className="gp-join-input" value={createName} onChange={e => setCreateName(e.target.value)} placeholder="e.g. My Nail Salon" autoFocus />
                                </div>
                                <div>
                                    <label className="gp-join-label">Description</label>
                                    <input type="text" className="gp-join-input" value={createDesc} onChange={e => setCreateDesc(e.target.value)} placeholder="Short description (optional)" />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    <div>
                                        <label className="gp-join-label">Phone</label>
                                        <input type="tel" className="gp-join-input" value={createPhone} onChange={e => setCreatePhone(e.target.value)} placeholder="+1 234 567 8900" />
                                    </div>
                                    <div>
                                        <label className="gp-join-label">Address</label>
                                        <input type="text" className="gp-join-input" value={createAddress} onChange={e => setCreateAddress(e.target.value)} placeholder="123 Main St" />
                                    </div>
                                </div>
                            </div>
                            {createMsg.text && <div className={`gp-join-msg ${createMsg.type}`}>{createMsg.text}</div>}
                        </div>
                        <div className="gp-modal-footer">
                            <button className="gp-btn-ghost" onClick={() => setShowCreateModal(false)} disabled={creating}>Cancel</button>
                            <button className="gp-btn-primary" onClick={handleCreateBusiness} disabled={creating || !createName.trim()}>
                                {creating ? 'Creating…' : '🏢 Create Business'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── JOIN BUSINESS MODAL ── */}
            {showJoinModal && (
                <div className="gp-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowJoinModal(false); }}>
                    <div className="gp-modal">
                        <div className="gp-modal-header">
                            <h3>🔗 Join a Business</h3>
                            <button className="gp-modal-close" onClick={() => setShowJoinModal(false)}>✕</button>
                        </div>
                        <div className="gp-modal-body">
                            <div className="gp-join-info">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                <div>
                                    <strong>How to join:</strong> Ask the business owner for their <strong>Business ID</strong> and paste it below. If you <em>created</em> the business, you're already the owner — no need to join.
                                </div>
                            </div>
                            <label className="gp-join-label">Business ID <span style={{ color: '#ef4444' }}>*</span></label>
                            <input
                                type="text"
                                className="gp-join-input"
                                value={joinId}
                                onChange={(e) => setJoinId(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleJoinBusiness()}
                                placeholder="e.g. 65f3a2b1c4d5e6f7a8b9c0d1"
                                autoFocus
                                spellCheck={false}
                            />
                            {joinMsg.text && (
                                <div className={`gp-join-msg ${joinMsg.type}`}>{joinMsg.text}</div>
                            )}
                        </div>
                        <div className="gp-modal-footer">
                            <button className="gp-btn-ghost" onClick={() => setShowJoinModal(false)} disabled={joining}>Cancel</button>
                            <button className="gp-btn-primary" onClick={handleJoinBusiness} disabled={joining || !joinId.trim()}>
                                {joining ? 'Joining…' : '🔗 Join Business'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── LEAVE BUSINESS MODAL ── */}
            {leavingBizId && (
                <div className="gp-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setLeavingBizId(null); }}>
                    <div className="gp-modal" style={{ maxWidth: '400px' }}>
                        <div className="gp-modal-header">
                            <h3 style={{ color: '#ef4444' }}>{language === 'vi' ? 'Rời khỏi Business' : 'Leave Business'}</h3>
                            <button className="gp-modal-close" onClick={() => setLeavingBizId(null)}>✕</button>
                        </div>
                        <div className="gp-modal-body">
                            <p style={{ margin: '0 0 16px', color: '#475569', fontSize: '0.95rem', lineHeight: 1.5 }}>
                                {language === 'vi' 
                                    ? `Bạn có chắc chắn muốn rời khỏi business "${leavingBizId.name || leavingBizId.id}" không? Bạn sẽ cần mã mời để tham gia lại.` 
                                    : `Are you sure you want to leave "${leavingBizId.name || leavingBizId.id}"? You will need an invite code to join again.`}
                            </p>
                        </div>
                        <div className="gp-modal-footer">
                            <button className="gp-btn-ghost" onClick={() => setLeavingBizId(null)} disabled={leaving}>
                                {language === 'vi' ? 'Hủy' : 'Cancel'}
                            </button>
                            <button className="gp-btn-primary" style={{ background: '#ef4444' }} onClick={() => handleLeaveBusiness(leavingBizId.id)} disabled={leaving}>
                                {leaving ? (language === 'vi' ? 'Đang xử lý...' : 'Leaving...') : (language === 'vi' ? 'Rời đi' : 'Leave')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── DELETE BUSINESS MODAL ── */}
            {deletingBizId && (
                <div className="gp-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setDeletingBizId(null); }}>
                    <div className="gp-modal" style={{ maxWidth: '400px' }}>
                        <div className="gp-modal-header">
                            <h3 style={{ color: '#dc2626' }}>{language === 'vi' ? 'Xóa Business' : 'Delete Business'}</h3>
                            <button className="gp-modal-close" onClick={() => setDeletingBizId(null)}>✕</button>
                        </div>
                        <div className="gp-modal-body">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <span style={{ fontSize: '18px' }}>⚠️</span>
                                <strong style={{ color: '#b91c1c' }}>{language === 'vi' ? 'Cảnh báo nguy hiểm' : 'Danger Warning'}</strong>
                            </div>
                            <p style={{ margin: '0 0 16px', color: '#475569', fontSize: '0.95rem', lineHeight: 1.5 }}>
                                {language === 'vi' 
                                    ? `Bạn có chắc chắn muốn xóa vĩnh viễn business "${deletingBizId.name || deletingBizId.id}" không? Hành động này không thể hoàn tác, và tất cả nhân viên sẽ bị mất quyền truy cập.` 
                                    : `Are you sure you want to permanently delete "${deletingBizId.name || deletingBizId.id}"? This action cannot be undone, and all employees will lose access.`}
                            </p>
                        </div>
                        <div className="gp-modal-footer">
                            <button className="gp-btn-ghost" onClick={() => setDeletingBizId(null)} disabled={deletingBiz}>
                                {language === 'vi' ? 'Hủy' : 'Cancel'}
                            </button>
                            <button className="gp-btn-primary" style={{ background: '#dc2626' }} onClick={() => handleDeleteBiz(deletingBizId.id)} disabled={deletingBiz}>
                                {deletingBiz ? (language === 'vi' ? 'Đang xóa...' : 'Deleting...') : (language === 'vi' ? 'Xác nhận xóa' : 'Confirm Delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Reusable field component
const Field = ({ label, name, value, isEditing, onChange, type = 'text', placeholder = '—', readOnly = false, fullWidth = false }) => (
    <div className={`gp-field ${fullWidth ? 'full-width' : ''}`}>
        <span className="gp-field-label">{label}</span>
        {isEditing && !readOnly ? (
            <input
                type={type}
                name={name}
                className="gp-field-input"
                value={value || ''}
                onChange={onChange}
                placeholder={placeholder}
            />
        ) : (
            <span className="gp-field-value">{value || <em className="gp-empty">Not set</em>}</span>
        )}
    </div>
);

export default UserProfile;
